// Partner Brand Ownership — the Prequalification tab's INTERNAL CONTRACT.
//
// The tab is now a shell over three independently-mounted panels plus a send
// wizard, each owned by a different workstream and each living in its own file
// beside this one. This module is the seam between them: it holds the prop
// shapes the shell passes down, and the handful of pure helpers that more than
// one panel renders.
//
// Two rules make the seam worth having:
//
//   1. The SHELL owns the list controls that are shared — the Mine/Everyone
//      scope and which panel is showing. A panel READS them and never writes
//      them, so the scope can never silently differ between two tabs.
//   2. A helper that formats a completion's headline figure, or turns a thrown
//      value into a sentence, lives HERE and nowhere else. These were local to
//      the one 1,000-line tab file; splitting that file without a shared home
//      would have produced three near-identical copies of `errorMessage` that
//      drift the first time one of them is improved.
//
// Portal-local by the isolation rule: design-system kit + portal-relative
// imports only.

import { formatCurrency } from "@/design-system/lib/format";
import type { StatusTone } from "@/design-system/components/ui";
import { PortalApiError } from "../../api/client";
import type {
  PrequalAnswerGroup,
  PrequalCompletions,
  PrequalOutcome,
  PrequalSort,
} from "../../api/prequalSite";

/** Which inner list is showing. Mirrored to ?view=; "completions" is the default. */
export type PrequalView = "completions" | "invitations" | "in-progress";

/** Newest / oldest. Maps to the server's `sort` param; "newest" is the default
 *  and reproduces today's fixed DESC ordering exactly.
 *
 *  DECLARED in `../../api/prequalSite` — it is a WIRE value, and the three list
 *  param types there need it. Re-exported here so a panel can pull the whole
 *  list vocabulary from one import. Imported as well as re-exported: a bare
 *  `export type { … } from` creates no local binding, and PREQUAL_SORT_OPTIONS
 *  below references the name. */
export type { PrequalSort };

/** Already the server default on all three endpoints. */
export const PREQUAL_PAGE_SIZE = 25;

export const PREQUAL_SORT_OPTIONS: { value: PrequalSort; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

/** The shell-owned list controls. A panel READS these and never writes them. */
export interface PrequalPanelProps {
  /** FALSE means "Everyone on this site", and is the default (D3).
   *
   *  That default is a CHANGE from the two separate toggles this replaced, and
   *  it is deliberate: an organic completion has no sender at all, so "Mine"
   *  would hide every lead the public funnel produced — on Completions, which
   *  is the tab that opens first. Defaulting to Everyone means nothing is
   *  hidden on arrival. */
  mineOnly: boolean;
}

export interface CompletionsPanelProps extends PrequalPanelProps {
  /** The shell's own mount fetch — which is ALSO how the site is resolved (D6).
   *  Seeds page 1 so the default view does not fetch the same page twice.
   *
   *  SEEDING RULE: use `initial` INSTEAD of fetching only while this panel's
   *  own controls are all at their defaults — page 1, empty search, sort
   *  "newest" — AND `mineOnly === false`. Any other combination fetches. The
   *  moment a control moves off its default, `initial` describes a different
   *  query than the one on screen and must be ignored. */
  initial: PrequalCompletions;
  /** Open the drill-in drawer. The shell writes ?completion= and preserves ?view=. */
  onOpen: (completionId: string) => void;
}

export type InvitesPanelProps = PrequalPanelProps;
export type DraftsPanelProps = PrequalPanelProps;

/** The `?completion=` drill-in, mounted by the SHELL as a slide-in drawer
 *  ALONGSIDE the tab rather than in place of it.
 *
 *  It used to replace the entire tab, tab bar included (D11), which meant the
 *  partner lost the list — and their place in it — to read one row. A drawer
 *  keeps the list behind it, so closing is a return rather than a re-navigation.
 *  The deep link is unchanged: `?completion=` still opens it on load.
 *
 *  Mounted CONDITIONALLY, so mounting is opening: there is no `open` prop to
 *  disagree with `completionId`, and every load starts fresh. */
export interface CompletionDetailDrawerProps {
  completionId: string;
  /** Clears ?completion= while leaving every other param — including ?view= —
   *  exactly as it was, so closing returns to the list the user came from. */
  onClose: () => void;
}

// ── Answer sections ─────────────────────────────────────────────────────────

/** Render order, mirroring `PrequalAnswerGroups.Order` in
 *  Core/Prequalification/PrequalAnswerDisplay.cs: who this is, what they told
 *  us, what we worked out from it, how ready the business looks, what the
 *  screening said, and the documents.
 *
 *  🔴 Declared HERE rather than beside the wire types it belongs to, because
 *  both test files that exercise this surface replace `../../api/prequalSite`
 *  wholesale with a `vi.mock` factory — a runtime value imported from there
 *  would be `undefined` under test while looking correct in the browser. The
 *  types stay in the api module; only this array had to move. */
export const PREQUAL_ANSWER_GROUP_ORDER: readonly PrequalAnswerGroup[] = [
  "About",
  "Reported",
  "Calculated",
  "Readiness",
  "Screening",
  "Documents",
];

/** Section headings. The group constants are identifiers shared with the
 *  server; these are the drawer's own chrome, and the only wording this client
 *  chooses — every row's label, value and explanation is composed server-side. */
export const PREQUAL_ANSWER_GROUP_LABELS: Record<PrequalAnswerGroup, string> = {
  About: "About",
  Reported: "What they told us",
  Calculated: "What we worked out",
  Readiness: "Readiness",
  Screening: "Screening",
  Documents: "Documents",
};

export interface NewPrequalificationWizardProps {
  open: boolean;
  /** Guarded by the wizard against closing mid-submit. */
  onClose: () => void;
  /** site.displayName — the "we'll email them a link to X" copy. */
  siteName: string;
  /** Called after a successful MAILED send so the shell can refresh. The
   *  in-person path navigates away instead and never calls this. */
  onSent: () => void | Promise<void>;
}

// ── Shared pure helpers ─────────────────────────────────────────────────────
// Moved verbatim out of the tab when it was split. One copy, imported by every
// panel that needs it.

export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function outcomeTone(outcome: PrequalOutcome): StatusTone {
  return outcome === "Pass" ? "ok" : "warn";
}

export function outcomeLabel(outcome: PrequalOutcome): string {
  return outcome === "Pass" ? "Prequalified" : "Needs a conversation";
}

/** The headline figure: a buyer's qualified amount, a seller's supportable
 *  price band. Null when the engine produced no computable figure. */
export function figureFor(row: {
  role: string;
  qualifiedAmount: number | null;
  supportablePriceLow: number | null;
  supportablePriceHigh: number | null;
  buyerDocumentPath?: string | null;
}): string | null {
  if (row.role === "Buyer") {
    if (row.qualifiedAmount == null) return null;
    // 🔴 "Up to" ONLY WHERE THE FIGURE IS THE CEILING. The engine sets the
    // headline to the TOP of the range on the internal-purchase arm and to the
    // MIDPOINT of the buying-power band everywhere else, so on the capital arm
    // the band's own high end exceeds the number this column was calling a
    // maximum. Same rule as the letter's At-a-Glance row and the funnel's
    // result screen; a broker reading this list and the letter it produced
    // should not meet two descriptions of one figure.
    const ceiling = row.buyerDocumentPath === "InternalBuyer";
    const amount = formatCurrency(row.qualifiedAmount);
    return ceiling ? `Up to ${amount}` : amount;
  }
  if (row.supportablePriceLow != null && row.supportablePriceHigh != null) {
    return `${formatCurrency(row.supportablePriceLow)} – ${formatCurrency(row.supportablePriceHigh)}`;
  }
  return null;
}

/** Native select — the shared kit has no Select; these are the Input tokens. */
export const SELECT_CLASS =
  "w-full h-10 px-space-3 py-[10px] font-body text-body sm:text-small text-onyx bg-white-pure " +
  "border border-border-light rounded-md transition-colors duration-fast ease-standard " +
  "focus:outline-none focus:border-gold focus:shadow-goldRing " +
  "disabled:bg-white-warm disabled:opacity-60 disabled:cursor-not-allowed";
